import qs from 'query-string'
import { createContext, useContext, useEffect, useReducer } from 'react'

import { BID_STATUS } from 'legacy/constants/bids'
import { getOffer } from 'legacy/services/offers'
import { getPusher } from 'legacy/services/pusher'
import { user } from 'legacy/utils/user'

export const LOAD_BOARD_ACTIONS = {
  // Offers
  UPDATE_OFFER: 'UPDATE_OFFER',
  UPSERT_OFFERS: 'UPSERT_OFFERS',
  BOOK_OFFER: 'BOOK_OFFER',
  CLEAR_BOOK_OFFER: 'CLEAR_BOOK_OFFER',
  CLEAR_FETCHED_OFFERS: 'CLEAR_FETCHED_OFFERS',
  MARK_OFFER_TAKEN: 'MARK_OFFER_TAKEN',
  SET_BUSINESS_MODE: 'SET_BUSINESS_MODE',

  // UX
  PERSIST_SCROLL_POSITION: 'PERSIST_SCROLL_POSITION',
  PERSIST_SEARCH_QUERY: 'PERSIST_SEARCH_QUERY',

  // Carrier
  UPDATE_CARRIER: 'UPDATE_CARRIER',

  // Bids
  UPDATE_BID: 'UPDATE_BID',
  UPSERT_BID: 'UPSERT_BID',
  UPSERT_BIDS: 'UPSERT_BIDS',
  CLOSE_BID_DIALOG: 'CLOSE_BID_DIALOG',
  OPEN_BID_DIALOG: 'OPEN_BID_DIALOG',
  REMOVE_BID: 'REMOVE_BID',
}

export const LoadBoardContext = createContext()

export function useLoadboardContext() {
  const context = useContext(LoadBoardContext)

  if (context === undefined) {
    throw new Error(`useLoadboardContext must be used within a LoadBoardContextProvider`)
  }

  return context
}

export function loadBoardReducer(state, action) {
  if (!action || !action.type) throw new Error('Action is required')
  const { payload, type } = action

  switch (type) {
    // Offers
    case LOAD_BOARD_ACTIONS.UPSERT_OFFERS: {
      const { newLoadOffers, totalOffersCount, fetchedSearchQuery } = payload
      const { loadOffersMap, persistedSearchQuery } = state

      // Discard old fetch requests returning after a newer request was made
      if (persistedSearchQuery !== fetchedSearchQuery) return state

      const newLoadOffersMap = newLoadOffers.reduce(
        (acc, loadOffer) => ({
          ...acc,
          [loadOffer.load.uuid]: loadOffer,
        }),
        loadOffersMap
      )

      return { ...state, loadOffersMap: newLoadOffersMap, totalOffersCount }
    }
    case LOAD_BOARD_ACTIONS.UPDATE_OFFER: {
      const loadOffer = payload
      const { loadOffersMap } = state

      // this should only update existing offers, not insert them on the state
      if (!loadOffersMap[loadOffer.load.uuid]) return { ...state }

      const newLoadOffersMap = {
        ...loadOffersMap,
        [loadOffer.load.uuid]: loadOffer,
      }

      return { ...state, loadOffersMap: newLoadOffersMap }
    }
    case LOAD_BOARD_ACTIONS.BOOK_OFFER: {
      const { id, position, triggerContext } = payload || {}
      return { ...state, bookingOffer: { id, position, triggerContext } }
    }
    case LOAD_BOARD_ACTIONS.CLEAR_BOOK_OFFER:
      return { ...state, bookingOffer: null }

    case LOAD_BOARD_ACTIONS.CLEAR_FETCHED_OFFERS: {
      return { ...state, loadOffersMap: {}, totalOffersCount: undefined }
    }
    case LOAD_BOARD_ACTIONS.MARK_OFFER_TAKEN: {
      const { loadUUID } = payload
      const { loadOffersMap, takenOffers } = state
      const newLoadOffersMap = Object.assign({}, loadOffersMap)
      delete newLoadOffersMap[loadUUID]
      return { ...state, takenOffers: [...takenOffers, loadUUID], loadOffersMap: newLoadOffersMap }
    }
    case LOAD_BOARD_ACTIONS.SET_BUSINESS_MODE:
      return { ...state, businessMode: payload.businessMode }

    // UX
    case LOAD_BOARD_ACTIONS.PERSIST_SCROLL_POSITION:
      return {
        ...state,
        persistedScrollPosition: payload.scrollPosition,
        isAwaitingScroll: payload.isAwaitingScroll,
      }
    case LOAD_BOARD_ACTIONS.PERSIST_SEARCH_QUERY:
      return { ...state, persistedSearchQuery: payload.searchQuery }

    // Carrier
    case LOAD_BOARD_ACTIONS.UPDATE_CARRIER:
      return {
        ...state,
        carrier: {
          ...state.carrier,
          ...(payload || {}),
        },
      }

    // BID
    case LOAD_BOARD_ACTIONS.OPEN_BID_DIALOG:
      return {
        ...state,
        bidOfferUUID: payload.offerUUID,
        bidLoadUUID: payload.load.uuid,
        bidLoad: payload.load,
        biddingBookItNowPrice: payload.price,
      }
    case LOAD_BOARD_ACTIONS.CLOSE_BID_DIALOG: {
      const shouldCloseBid = state.bidOfferUUID === payload
      return {
        ...state,
        bidOfferUUID: shouldCloseBid ? undefined : state.bidOfferUUID,
        bidLoadUUID: shouldCloseBid ? undefined : state.bidLoadUUID,
      }
    }
    case LOAD_BOARD_ACTIONS.UPSERT_BID: {
      const bids = { ...state.bids }
      const bid = payload

      if (bids[bid.load.uuid]?.status === BID_STATUS.REMOVED) {
        /* Back-end send us an expired event after a bid is removed.
         * However, if we update the state here we would display an
         * `expired` notification instead of a `removed` one.
         * So we just return state without changes  */
        return state
      }

      bids[bid.load.uuid] = bid

      let loadUUIDToRefreshAfterBid
      if (bid.status === BID_STATUS.REFUSED) {
        /* When a bid is refused or expired, we need to trigger a load refresh so the
         * Load Details state correctly reflects the `Bid Again in XX:XX` time bid soft-lock.
         * This state is used on the Load Details to trigger the Load Offer fetch. */
        loadUUIDToRefreshAfterBid = bid.load.uuid
      }
      return {
        ...state,
        bids: bids,
        loadUUIDToRefreshAfterBid,
      }
    }
    case LOAD_BOARD_ACTIONS.UPSERT_BIDS: {
      const bids = { ...state.bids }
      payload.forEach(bid => {
        bids[bid.load.uuid] = bid
      })
      return {
        ...state,
        bids: bids,
      }
    }
    case LOAD_BOARD_ACTIONS.REMOVE_BID: {
      const loadUUID = payload
      const updatedBids = { ...state.bids }
      delete updatedBids[loadUUID]
      return {
        ...state,
        bids: updatedBids,
      }
    }
    default:
      return state
  }
}

const [, locationSearch] = (window.location.hash || '').split('?')
const { bookingOfferId: bookingOfferIdInSearch } = qs.parse(locationSearch) || {}

const bookingOffer = bookingOfferIdInSearch
  ? {
      id: bookingOfferIdInSearch,
    }
  : null

// Load Offers is a Hash Map based on ref number to deal with duplicates
export const initialState = {
  bidLoadUUID: undefined,
  bidOfferUUID: undefined,
  bidLoad: {},
  biddingBookItNowPrice: undefined,
  bookingOffer,
  businessMode: undefined,
  carrier: {
    _isLoaded: false,
    _isLoading: false,
  },
  isAwaitingScroll: false,
  loadOffersMap: {},
  bids: {},
  persistedScrollPosition: 0,
  persistedSearchQuery: null,
  takenOffers: [],
  totalOffersCount: undefined,
  bidToRefuse: null,
  loadUUIDToRefreshAfterBid: undefined,
}

export function onBidWSEvent(incomingBid, action, dispatch) {
  if (action === 'accepted') {
    dispatch({
      type: LOAD_BOARD_ACTIONS.MARK_OFFER_TAKEN,
      payload: { loadUUID: incomingBid.load.uuid },
    })
  }

  if (action === 'expired') {
    /* The bidding API doesn't change the bidding state for expired bids to keep track of the last bid state,
     * so we change it to timed_out when they were previously awarded or pending to display the
     * correct message */
    if ([BID_STATUS.AWARDED, BID_STATUS.PENDING].includes(incomingBid.status)) {
      incomingBid.status = BID_STATUS.TIMED_OUT
    }
    // We don't want to display any banner if the bid was refused and expired
    if (incomingBid.status === BID_STATUS.REFUSED) {
      return
    }
  }

  if (['refused', 'expired'].includes(action)) {
    /* When a bid is refused or expired, we need to trigger an offer refresh so the
     * Load Board state correctly reflects the `Bid Again in XX:XX` time bid soft-lock.
     * This impacts the state of the main load board if the offer is listed there. */
    getOffer(incomingBid.offer.uuid).then(data => {
      const offer = data?.data
      dispatch({ type: LOAD_BOARD_ACTIONS.UPDATE_OFFER, payload: offer })
    })
  }

  /**
   * the backend emits 2 events when the bid receives a counter-offer
   * we should ignore them in order to prevent refused banners flashing on the screen
   */
  if (incomingBid.refuse_reason === 'counterbid') return

  dispatch({ type: LOAD_BOARD_ACTIONS.UPSERT_BID, payload: incomingBid })
}

export function LoadBoardContextProvider(props) {
  const [state, dispatch] = useReducer(loadBoardReducer, initialState)

  useEffect(() => {
    if (user.isLoggedIn()) {
      // Pusher Websocket Service
      const pusher = getPusher()
      if (pusher) {
        // Carrier PVT Channel
        const companyId = user.get('companyId')
        const pvtChannel = pusher.subscribe(`private-carrier=${companyId}`)

        // Binding event for Load Board stream
        pvtChannel.bind('loadboard-state:changed', message => {
          const { type, action, data } = message || {}
          if (type === 'bid') {
            const incomingBid = data
            onBidWSEvent(incomingBid, action, dispatch)
          }
        })

        return () => {
          pvtChannel.unbind()
          pusher.unsubscribe(pvtChannel)
        }
      }
    }
  }, [dispatch])

  return <LoadBoardContext.Provider value={[state, dispatch]} {...props} />
}
